import _ from 'underscore';
import { Component } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from 'styled-components';
import { AutoSizer, Table, Column, SortDirection, defaultTableRowRenderer } from 'react-virtualized';
import SortIndicator from 'react-virtualized/dist/commonjs/Table/SortIndicator';
import { getDefaultFont } from '../../common';

const DEFAULT_TABLE_ROW_HEIGHT = 50;
const DEFAULT_TABLE_HEADER_ROW_HEIGHT = 50;
const DEFAULT_TABLE_HEIGHT = DEFAULT_TABLE_ROW_HEIGHT * 15.5;

const RowRenderer = (props) => <div>{defaultTableRowRenderer(props.selectProps)}</div>;

/*
  a quick overview of some of the data structures used for this component:

  allColumnProps = columnProps[]
  columnProps = {
    label: string,
    dataKey: string, (used for sortByColumn)
    width: number,
    cellRenderer: function(unsortedRow) => rendered cell,
    sortFn: function(unsortedRow) => sort value
  }

  unsortedRows = unsortedRow[]
  unsortedRow has data required for columnProps.cellRenderer
*/
class ReactVirtualizedTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      sortByColumn: props.initialSortByColumn,
      sortDirection: props.initialSortDirection ? props.initialSortDirection : SortDirection.DESC,
    };
  }

  render() {
    const rows = this.props.sortedRows || this.rowSorter(this.props.unsortedRows);
    return (
      <AutoSizer onResize={this.props.onResize}>
        {({ height, width }) => {
          const scaledColumnProps = this.props.dynamicColumnWidths
            ? _.map(this.props.allColumnProps, (columnProp) => this.scaleColumnWidths(columnProp, width))
            : this.props.allColumnProps;
          const omittedRowRenderProps = ['key'];
          if (this.props.disableRowClick) omittedRowRenderProps.push('onRowClick');

          return (
            <Table
              width={this.props.width || width}
              height={this.props.height || height}
              headerHeight={this.props.headerHeight || 40}
              headerClassName={this.props.headerClassName}
              rowHeight={this.props.rowHeight || 65}
              className={`virtualized-table ${this.props.tableClassName}`}
              rowClassName={`virtualized-table-row ${this.props.rowClassName}`}
              gridClassName={`virtualized-table-grid ${this.props.gridClassName}`}
              onRowClick={({ index }) => this.props.onRowClick && this.props.onRowClick(rows[index])}
              overscanRowCount={
                this.props.overscanRowCount || 20
              } /* https://github.com/bvaughn/react-virtualized/blob/master/docs/overscanUsage.md */
              noRowsRenderer={this.props.noRowsRenderer}
              rowCount={rows.length}
              rowRenderer={(props) => (
                <RowRenderer
                  key={props.rowData.id || props.key}
                  style={props.style}
                  selectProps={_.omit(props, omittedRowRenderProps)}
                />
              )}
              rowGetter={(row) => rows[row.index]}
              sort={this.props.sortTable || this.sortTable}
              sortBy={this.props.sortByColumn || this.state.sortByColumn}
              sortDirection={this.props.sortDirection || this.state.sortDirection}
              onScroll={this.props.onScroll}
              onRowsRendered={this.props.onRowsRendered}
            >
              {_.map(scaledColumnProps, (columnProps, index) => (
                <Column key={columnProps.dataKey ?? index} {...columnProps} />
              ))}
            </Table>
          );
        }}
      </AutoSizer>
    );
  }

  // The width field of a column prop can be passed in as a percentage, in which case we
  // set the final width value based upon the table width
  scaleColumnWidths = (columnProp, tableWidth) => ({ ...columnProp, width: tableWidth * columnProp.width });

  sortTable = (props) => {
    const shouldFlipSortDirection = props.sortBy === this.state.sortByColumn;
    const sortDirection = (() => {
      if (shouldFlipSortDirection) {
        return this.state.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
      }
      return this.state.sortDirection;
    })();

    this.setState({
      sortByColumn: props.sortBy,
      sortDirection,
    });
  };

  rowSorter = (unsortedRows) => {
    const sorterFunction = (
      _.find(this.props.allColumnProps, (columnProps) => columnProps.dataKey === this.state.sortByColumn) || {}
    ).sortFn;

    const sortedRows = _.sortBy(unsortedRows, (row) => (sorterFunction ? sorterFunction(row) : row));

    return this.state.sortDirection === SortDirection.ASC ? sortedRows.reverse() : sortedRows;
  };
}

const tableWrapperCalculatedHeight = ({
  unsortedRows,
  sortedRows,
  rowHeight,
  headerHeight,
  customTableHeight,
  forceCustomTableHeight,
}) => {
  if (forceCustomTableHeight) return customTableHeight;

  const tableRowHeight = rowHeight || DEFAULT_TABLE_ROW_HEIGHT;
  const tableHeaderRowHeight = headerHeight || DEFAULT_TABLE_HEADER_ROW_HEIGHT;
  const tableHeight = customTableHeight || DEFAULT_TABLE_HEIGHT;

  let tableCalculatedHeight;
  const rows = sortedRows || unsortedRows;
  if (_.isEmpty(rows)) {
    tableCalculatedHeight = tableHeaderRowHeight + 2 * tableRowHeight;
  } else {
    tableCalculatedHeight = _.size(rows) * tableRowHeight + tableHeaderRowHeight;
  }

  return Math.min(tableCalculatedHeight, tableHeight);
};

const ReactVirtualizedTableWithCalculatedHeight = (props) => {
  const rebuildTooltip = _.debounce(() => ReactTooltip.rebuild(), 100);
  const rebuildToolTipIfToolTipsExist = () => !_.isEmpty(props.toolTipProps) && rebuildTooltip();
  rebuildToolTipIfToolTipsExist();

  const onResize = () => {
    if (props.onResize) props.onResize();
    rebuildToolTipIfToolTipsExist();
  };

  const onScroll = () => {
    if (props.onScroll) props.onScroll();
    rebuildToolTipIfToolTipsExist();
  };

  return (
    <TableWrapper
      height={tableWrapperCalculatedHeight(props)}
      showRowBorderBottom={props.showRowBorderBottom}
      allowOverflow={props.allowOverflow}
      ref={props.parentRef}
    >
      <ReactVirtualizedTable
        onResize={onResize}
        onScroll={onScroll}
        headerHeight={props.headerHeight || DEFAULT_TABLE_HEADER_ROW_HEIGHT}
        rowHeight={props.rowHeight || DEFAULT_TABLE_ROW_HEIGHT}
        {..._.omit(props, ['onResize', 'onScroll', 'headerHeight', 'rowHeight'])}
      />
      {!_.isEmpty(props.toolTipProps) &&
        props.toolTipProps.map(({ id, place, effect }) => (
          <ReactTooltip
            positionStrategy="fixed"
            key={id}
            id={id}
            place={place || 'bottom'}
            effect={effect || 'solid'}
          />
        ))}
    </TableWrapper>
  );
};

const TableWrapper = styled.div.attrs({
  className: 't18-table',
})`
  height: ${(props) => `${props.height}px`};
  width: 100%;
  overflow: ${({ allowOverflow }) => (allowOverflow ? 'visible !important' : 'hidden')};

  .ReactVirtualized__Table__Grid {
    outline: none;
  }

  .ReactVirtualized__Table__row {
    outline: none;
    ${({ allowOverflow }) => allowOverflow && 'overflow: visible !important;'}
    ${({ showRowBorderBottom }) => showRowBorderBottom && 'box-sizing: border-box;'}
  }
  .ReactVirtualized__Table__rowColumn {
    ${({ allowOverflow }) => allowOverflow && 'overflow: visible !important;'}
  }
  .ReactVirtualized__Table__headerColumn {
    display: flex;
    align-items: center;
  }
`;

const DefaultNoRowsRenderer = ({ className, noRowsMessage }) => (
  <NoRowsRender className={className} marginTop={DEFAULT_TABLE_ROW_HEIGHT / 2}>
    {noRowsMessage}
  </NoRowsRender>
);

const HeaderRenderer = ({ className, dataKey, sortBy, sortDirection, title, rightAligned }) => (
  <div className={className}>
    <ColumnTitle>{title}</ColumnTitle>
    {sortBy === dataKey && <SortIndicator sortDirection={sortDirection} />}
  </div>
);

const NoRowsRender = styled.div`
  padding: 14px;
  text-align: center;
  margin-top: ${({ marginTop }) => `${marginTop}px`};
`;

const ColumnTitle = styled.div`
  font-family: ${getDefaultFont()};
  font-size: 12px;
  color: #253256;
  opacity: 0.5;
  letter-spacing: 1px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  display: inline-block;
`;

const DefaultHeaderRenderer = styled(HeaderRenderer)`
  display: flex;
  align-items: center;
  width: 100%;
  ${({ rightAligned }) => rightAligned && 'justify-content: flex-end;'}
`;

export {
  ReactVirtualizedTable,
  ReactVirtualizedTableWithCalculatedHeight,
  DefaultNoRowsRenderer,
  DefaultHeaderRenderer,
};
